// Shared by the Rocks components. Quarters are calendar quarters keyed "YYYY-Qn"; dates are local
// "YYYY-MM-DD" (same as routes/rocks.ts).

const MONTHS = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];

function iso(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parse(date: string): Date {
    const [ y, m, d ] = date.split('-').map(Number);
    return new Date(y, m - 1, d);
}

export function today(): string {
    return iso(new Date());
}

export function addDays(date: string, days: number): string {
    const d = parse(date);
    d.setDate(d.getDate() + days);
    return iso(d);
}

export function quarterOf(date: Date): string {
    return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
}

export function currentQuarter(): string {
    return quarterOf(new Date());
}

// Everyone who owns a rock: its owner, then its additional owners (who count exactly the same; routes/rocks.ts)
export function rockOwnerIDs(rock: any): number[] {
    return [ rock.OwnerEmployeeID, ...(rock.AdditionalOwnerIDs || []) ].filter(id => id).map(Number);
}

// Every team a rock is on: its team, then its additional teams (which count exactly the same; routes/rocks.ts)
export function rockTeamsOf(rock: any): string[] {
    return [ rock.Team, ...(rock.AdditionalTeams || []) ].filter(t => t);
}

export function isRockOwner(rock: any, employeeID: any): boolean {
    return !!employeeID && rockOwnerIDs(rock).includes(Number(employeeID));
}

// A rock's status pill: its label and class (ok/bad/done), on the Rocks cards and rock cards on boards
export function rockStatus(status: string): { label: string, cls: string } {
    return ({
        on: { label: 'On track', cls: 'ok' },
        off: { label: 'Off track', cls: 'bad' },
        done: { label: 'Done', cls: 'done' },
        notdone: { label: 'Not done', cls: 'bad' }
    } as any)[status] || { label: '', cls: '' };
}

export function quarterLabel(q: string): string {
    return `${q.slice(5)} ${q.slice(0, 4)}`;
}

export function quarterBounds(q: string): { start: string, end: string } {
    const year = Number(q.slice(0, 4)), n = Number(q.slice(6));
    return { start: iso(new Date(year, (n - 1) * 3, 1)), end: iso(new Date(year, n * 3, 0)) };
}

export function nextQuarter(q: string): string {
    const year = Number(q.slice(0, 4)), n = Number(q.slice(6));
    return n == 4 ? `${year + 1}-Q1` : `${year}-Q${n + 1}`;
}

export function prevQuarter(q: string): string {
    const year = Number(q.slice(0, 4)), n = Number(q.slice(6));
    return n == 1 ? `${year - 1}-Q4` : `${year}-Q${n - 1}`;
}

function daysBetween(a: string, b: string): number {
    return Math.round((parse(b).getTime() - parse(a).getTime()) / 86400000);
}

// Which of the quarter's 13 weeks a date falls in
export function weekOfQuarter(q: string, date: string): number {
    return Math.max(1, Math.min(13, Math.floor(daysBetween(quarterBounds(q).start, date) / 7) + 1));
}

export function daysLeft(q: string): number {
    return Math.max(0, daysBetween(today(), quarterBounds(q).end));
}

// "Oct 8"
export function shortDate(date: string): string {
    const d = parse(date);
    return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// Milestones still open with their date passed
export function lateMilestones(rock: any): any[] {
    const t = today();
    return (rock.Milestones || []).filter((m: any) => !m.Done && m.DueDate < t);
}

// The file name when a milestone's link is an uploaded file (API rocks.ts POST /milestone/file), without the upload
// time it was stored with; null for any other link
export function milestoneFileName(link: string): string | null {
    const match = /\/api\/appfile\/playbook_rock_file\/[^/]+\/([^/?#]+)$/.exec(String(link || ''));
    if(!match) {
        return null;
    }
    let name = match[1];
    try {
        name = decodeURIComponent(name);
    } catch(e) {
        // shown as stored
    }
    return name.replace(/_\d{13}(?=\.[^.]*$|$)/, '');
}

function escapeHtml(s: string): string {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// A rock's Issue, as a meeting board item to start from (QuickAddAgendaItem's preset): its missed milestones in the
// name, and its owners, Done statement, milestones and quarter in the description. Assigned to the owner.
// teamName: how to show its team (scoreboard.ts teamLabel); ownerName: an employee ID's name.
export function rockIssue(rock: any, teamName: string, ownerName: (id: any) => string): { Name: string, Description: string, AssigneeEmployeeID: any } {
    const milestones = rock.Milestones || [];
    const late = rock.Status == 'done' ? [] : lateMilestones(rock);
    const next = milestones.find((m: any) => !m.Done);
    const milestoneLine = (m: any) => `${escapeHtml(m.Text)} · ${shortDate(m.DueDate)} · ${escapeHtml(ownerName(m.OwnerEmployeeID || rock.OwnerEmployeeID))}`;

    const name = rock.Name + (late.length ? `: ${late.length} missed milestone${late.length == 1 ? '' : 's'}` : '');
    const lines = [
        rock.DoneStatement ? `Done looks like: ${escapeHtml(rock.DoneStatement)}` : '',
        milestones.length ? `Milestones: ${milestones.filter((m: any) => m.Done).length} of ${milestones.length} done` : 'No milestones yet',
        ...late.map((m: any) => `Missed: ${milestoneLine(m)}`),
        next && !late.includes(next) ? `Next: ${milestoneLine(next)}` : '',
        `${quarterLabel(rock.Quarter)} ends ${shortDate(quarterBounds(rock.Quarter).end)}`
    ].filter(l => l);
    const owners = rockOwnerIDs(rock).map(ownerName).join(', ') || 'No owner';
    const description = `<p><strong>${escapeHtml(teamName)} ${rock.TeamRock ? 'team rock' : 'rock'}</strong> · Owner: ${escapeHtml(owners)}</p>`
        + `<ul>${lines.map(l => `<li><p>${l}</p></li>`).join('')}</ul>`;
    return { Name: name, Description: description, AssigneeEmployeeID: rock.OwnerEmployeeID || null };
}
